/** UI “Custom Layers” ≡ API MapLevel. */

import type {
  IBoundaryGeometriesPageParams,
  IBoundaryGeometriesPageResponse,
  IBoundaryGeometryResponse,
} from "@/shared/interfaces/boundary-geometry.interface";

export type MapLevelStatus = "DRAFT" | "ACTIVE";

export type MapLevelListView = "working" | "published" | "active";

export interface ICreateMapLevelUploadUrlRequest {
  districtId: string;
  filename: string;
  mimetype: string;
  size: number;
}

export interface IMapLevelUploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  expiresIn: number;
}

export interface IImportMapLevelRequest {
  districtId: string;
  zipFileId: string;
}

export interface IImportMapLevelResponse {
  mapLevelId: string;
  name: string;
  documentId: string;
  zipFileId: string;
  fileName: string;
  boundaryCount: number;
  processedAt: string;
}

export interface IMapLevelCreatedBy {
  id: string;
  fullName: string;
}

export interface IMapLevelBoundary {
  id: string;
  mapLevelId: string;
  name: string;
  description: string | null;
  color: string;
  geometryGeoJson: string;
  sourceMetadata: unknown;
  createdAt: string;
  updatedAt: string;
}

export interface IMapLevel {
  id: string;
  districtId: string;
  name: string;
  description: string | null;
  status: MapLevelStatus;
  visibility: boolean;
  documentId: string | null;
  createdById: string | null;
  createdBy: IMapLevelCreatedBy | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  boundaries?: IMapLevelBoundary[];
}

export interface IMapLevelListResponse {
  items: IMapLevel[];
  total: number;
}

export interface IGetMapLevelsParams {
  districtId: string;
  view?: MapLevelListView;
  /** Always include these IDs (bypass view status/visibility; still not archived/deleted). */
  includeIds?: string[];
  skip?: number;
  take?: number;
}

export interface IGetMapLevelParams {
  districtId: string;
  levelId: string;
}

export interface IGetMapLevelBoundaryParams {
  districtId: string;
  levelId: string;
  boundaryId: string;
}

export interface IGetMapLevelBoundaryGeometriesParams
  extends IGetMapLevelParams, IBoundaryGeometriesPageParams {}

export interface IDeleteMapLevelRequest {
  districtId: string;
  levelId: string;
}

export interface IUpdateMapLevelRequest {
  districtId: string;
  levelId: string;
  body: {
    name?: string;
    description?: string | null;
    visibility?: boolean;
  };
}

export interface IUpsertMapLevelBoundaryItem {
  id?: string | null;
  geometry: GeoJSON.Geometry;
  name?: string;
  description?: string | null;
  color?: string;
  deleted?: boolean;
}

export interface IUpsertMapLevelBoundariesRequest {
  districtId: string;
  levelId: string;
  body: {
    boundaries: IUpsertMapLevelBoundaryItem[];
  };
}

export interface IMapLevelBoundaryListResponse {
  items: IMapLevelBoundary[];
}

export interface IMapLevelDraftBoundaryGeometry {
  id: string;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

export interface IResolveMapLevelGapFillRequest {
  districtId: string;
  levelId: string;
  body: {
    lng: number;
    lat: number;
    draftBoundaries?: IMapLevelDraftBoundaryGeometry[];
    deletedBoundaryIds?: string[];
  };
}

export interface IResolveMapLevelAutoFitRequest {
  districtId: string;
  levelId: string;
  body: {
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    editingBoundaryId: string;
    draftBoundaries?: IMapLevelDraftBoundaryGeometry[];
    deletedBoundaryIds?: string[];
  };
}

export interface IResolveMapLevelGeometryResponse {
  geometryGeoJson: string;
}

export interface IFinalizeMapLevelRequest {
  districtId: string;
  levelId: string;
}

export interface IArchiveMapLevelRequest {
  districtId: string;
  levelId: string;
}

export interface IDuplicateMapLevelRequest {
  districtId: string;
  levelId: string;
  body?: {
    name?: string;
  };
}

export type {
  IBoundaryGeometriesPageParams,
  IBoundaryGeometriesPageResponse,
  IBoundaryGeometryResponse,
};

/**
 * Map an edited Note field to the upsert `description` value.
 * Empty / whitespace → `null` (clears on the API). Otherwise trimmed string.
 */
export function toMapLevelBoundaryDescriptionUpsertValue(note: string): string | null {
  const trimmed = note.trim();
  return trimmed === "" ? null : trimmed;
}
